import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkMockInterviewQuota, trackMockInterviewUsage } from '../services/usageTracking';
import { sendMessage } from '../services/agent';
import { textToSpeech } from '../services/voiceService';
import * as documentStorage from '../services/documentStorage';
import { validateJobRole, validateUserResponse } from '../utils/inputValidation';
import { getFirestore, doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';

export default function MockInterview({ user, darkMode, selectedCoach: propSelectedCoach, onSelectCoach }) {
  const navigate = useNavigate();
  // Use prop if provided, otherwise get from localStorage
  const [selectedCoach, setSelectedCoach] = useState(() => {
    if (propSelectedCoach) return propSelectedCoach;
    const voiceId = localStorage.getItem('fidora_selected_voice') || 'nova';
    const voiceNames = {
      'nova': 'Nova',
      'sage': 'Atlas',
      'george': 'Sage',
      'marcus': 'Phoenix',
      'sarah': 'Aurora',
      'maya': 'Catalyst'
    };
    return voiceNames[voiceId] || 'Nova';
  });
  const [interviewStep, setInterviewStep] = useState('setup'); // setup | interview | feedback
  const [interviewType, setInterviewType] = useState('behavioral');
  const [difficulty, setDifficulty] = useState('mid-level');
  const [jobRole, setJobRole] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState([]);
  const [userResponse, setUserResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [quota, setQuota] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(new Audio());
  const [jobRoleSuggestion, setJobRoleSuggestion] = useState(null);
  const [validationError, setValidationError] = useState(null);
  // Voice input state
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  // Coaching/analysis mode state
  const [analysisMode, setAnalysisMode] = useState('summary'); // 'live' | 'summary'
  const [alignWithCoachData, setAlignWithCoachData] = useState(false);
  const [hasCoachData, setHasCoachData] = useState(false);
  const [coachStory, setCoachStory] = useState(null);
  const [liveFeedbackByIndex, setLiveFeedbackByIndex] = useState({});
  const [liveAnalysis, setLiveAnalysis] = useState({}); // STAR analysis per question
  // Text-only coaching to minimize voice usage
  const [readAloud, setReadAloud] = useState(false);
  // Retry loop
  const [awaitingRetry, setAwaitingRetry] = useState(false);
  // Intake preferences for adaptive session
  const [intake, setIntake] = useState({ additionalQuestions: '', notesForCoach: '', feelings: '', nextSteps: '' });
  // Adaptive focus toggle
  const [adaptiveFocusEnabled, setAdaptiveFocusEnabled] = useState(true);
  // Track interview start time for duration calculation
  const [interviewStartTime, setInterviewStartTime] = useState(null);

  // Colors
  const colors = {
    dark: {
      bg: '#1e1b4b',
      card: 'rgba(49, 46, 129, 0.4)',
      border: 'rgba(224, 231, 255, 0.15)',
      text: '#ffffff',
      subtext: '#e0e7ff',
      accent: '#818cf8',
      accentHover: '#6366f1',
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
    },
    light: {
      bg: '#fef3c7',
      card: 'rgba(255, 255, 255, 0.85)',
      border: 'rgba(30, 41, 59, 0.1)',
      text: '#1e293b',
      subtext: '#64748b',
      accent: '#8b5cf6',
      accentHover: '#7c3aed',
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
    }
  };

  const theme = darkMode ? colors.dark : colors.light;

  useEffect(() => {
    loadQuota();
    // Load coach data to determine if alignment can be enabled
    (async () => {
      try {
        if (!user || !user.uid) return;
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const story = data?.story || null;
          const hasData = story && typeof story === 'object' && Object.keys(story).length > 0;
          setHasCoachData(!!hasData);
          setCoachStory(hasData ? story : null);
        } else {
          setHasCoachData(false);
        }
      } catch (e) {
        console.warn('Unable to load coach data:', e?.message || e);
        setHasCoachData(false);
      }
    })();
  }, [user]);

  // Update selectedCoach when prop changes
  useEffect(() => {
    if (propSelectedCoach) {
      setSelectedCoach(propSelectedCoach);
    }
  }, [propSelectedCoach]);

  const loadQuota = async () => {
    if (!user || !user.uid) return;
    
    try {
      const quotaData = await checkMockInterviewQuota(user.uid);
      setQuota(quotaData);
      
      if (!quotaData.allowed && quotaData.upgradeRequired) {
        // User needs to upgrade
        console.log('User needs to upgrade for mock interviews');
      }
    } catch (error) {
      console.error('Error loading mock interview quota:', error);
    }
  };

  const interviewTypes = {
    behavioral: {
      name: 'Behavioral (STAR)',
      desc: 'Tell me about a time when... questions using the STAR method',
      icon: null
    },
    technical: {
      name: 'Technical/Skills',
      desc: 'Role-specific technical questions and problem-solving',
      icon: null
    },
    case: {
      name: 'Case Interview',
      desc: 'Business case analysis and strategic thinking',
      icon: null
    },
    leadership: {
      name: 'Leadership/Executive',
      desc: 'High-level strategic and leadership questions',
      icon: null
    }
  };

  const difficulties = {
    'entry-level': 'Entry Level (0-2 years)',
    'mid-level': 'Mid Level (3-7 years)',
    'senior': 'Senior (8-15 years)',
    'executive': 'Executive (15+ years)'
  };

  const startInterview = async () => {
    if (!quota || !quota.allowed) {
      alert(quota?.reason || 'Unable to start mock interview. Please check your subscription.');
      return;
    }

    // Validate job role
    const validation = validateJobRole(jobRole);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    // Clear any previous errors
    setValidationError(null);
    setJobRoleSuggestion(null);

    // Use the original job role or accepted suggestion
    const finalJobRole = jobRole.trim();

    setLoading(true);

    try {
      // Build questions either from backend or local fallback
      let questionList = [];
      // Include user-provided additional questions (intake)
      const extraQs = (intake.additionalQuestions || '').split('\n').map(q => q.trim()).filter(Boolean);

      try {
        // Preferred: ask backend for FRESH, CURRENT questions
        const currentYear = new Date().getFullYear();
        // Adaptive focus from prior sessions (if any)
        let adaptiveFocusBlock = '';
        try {
          const stored = localStorage.getItem(`fidora_adaptive_focus_${user?.uid || 'anon'}`);
          if (adaptiveFocusEnabled && stored) {
            const parsed = JSON.parse(stored);
            const focus = Array.isArray(parsed?.focus) ? parsed.focus.slice(0, 2) : [];
            if (focus.length > 0) {
              adaptiveFocusBlock = `\n\n## ADAPTIVE FOCUS (from recent sessions)\n- ${focus.join('\n- ')}\nKeep these in mind when selecting/phrasing questions.`;
            }
          }
        } catch {}
        const systemPrompt = `You are ${selectedCoach || 'Nova'}, an expert interview coach conducting a ${interviewType} mock interview for a ${difficulty} ${finalJobRole} position in ${currentYear}.

**YOUR TASK**: Generate 6-8 REAL, CURRENT interview questions that top companies are asking RIGHT NOW for this role and level.

**REQUIREMENTS**:
- Questions must be relevant to ${currentYear} industry trends
- Match the ${difficulty} seniority level (adjust complexity accordingly)
- ${interviewType === 'behavioral' ? 'Use STAR method format (Tell me about a time...)' : ''}
- ${interviewType === 'technical' ? 'Ask about modern tools, frameworks, and best practices used TODAY' : ''}
- ${interviewType === 'case' ? 'Use realistic business scenarios from current market conditions' : ''}
- NO generic/outdated questions
- Make each question specific and actionable${adaptiveFocusBlock}

Return ONLY the questions as a numbered list, one per line. No introduction, no conclusion.`;

        const response = await sendMessage(
          `Generate FRESH, CURRENT ${interviewType} interview questions for a ${difficulty} ${finalJobRole} role based on what top companies are asking in ${currentYear}. Make them specific and relevant to today's market.`,
          [{ role: 'system', content: systemPrompt }],
          user.displayName || user.email
        );

        questionList = response.reply
          .split('\n')
          .filter(line => line.trim().match(/^\d+[.)\-]/))
          .map(q => q.replace(/^\d+[.)\-]\s*/, '').trim())
          .filter(q => q.length > 0);
      } catch (backendErr) {
        console.warn('Backend unavailable, using local fallback questions:', backendErr?.message || backendErr);
        // Fallback questions for local/demo use
        const role = finalJobRole || 'role';
        if (interviewType === 'behavioral') {
          questionList = [
            `Tell me about a time you led a difficult project as a ${role}.`,
            `Describe a situation where you had conflicting priorities. What did you do?`,
            `Share an example of a mistake you made and how you handled it.`,
            `Tell me about a time you influenced a decision without authority.`,
            `Describe a conflict with a teammate and how you resolved it.`,
            `Walk me through a time you delivered results under a tight deadline.`,
          ];
        } else if (interviewType === 'technical') {
          questionList = [
            `What core skills are essential for a ${role} at the ${difficulty} level?`,
            `Describe a challenging technical problem you solved recently.`,
            `How do you approach debugging complex, ambiguous issues?`,
            `Explain a system or solution you designed end‑to‑end.`,
            `What tradeoffs did you consider in your last major decision?`,
            `How do you ensure quality, performance, and maintainability?`,
          ];
        } else if (interviewType === 'case') {
          questionList = [
            `A client’s KPI is down 20%. What is your structured approach?`,
            `Estimate the market size for a new ${role}-adjacent product.`,
            `Recommend a go‑to‑market strategy with 2 options and tradeoffs.`,
            `How would you measure success and set leading indicators?`,
            `Identify top risks and your mitigation plan.`,
            `Create a 30/60/90‑day plan for execution.`,
          ];
        } else {
          // leadership
          questionList = [
            `Describe your leadership philosophy for a ${role} team.`,
            `Tell me about a time you set vision and aligned stakeholders.`,
            `How do you build a high‑trust, high‑accountability culture?`,
            `Share an example of a tough call you made and how you communicated it.`,
            `How do you develop talent and succession plans?`,
            `How do you balance speed, quality, and risk at scale?`,
          ];
        }
      }

      // Merge any intake additional questions at the end (dedup by simple match)
      if (extraQs.length > 0) {
        const existingSet = new Set(questionList.map(q => q.toLowerCase()));
        extraQs.forEach(q => { if (!existingSet.has(q.toLowerCase())) questionList.push(q); });
      }

      if (!questionList || questionList.length === 0) {
        throw new Error('Failed to generate interview questions');
      }

      setQuestions(questionList);
      setCurrentQuestion(0);
      setInterviewStep('interview');
      setInterviewStartTime(Date.now()); // Track start time
      
      // Track usage at start
      await trackMockInterviewUsage(user.uid);
      await loadQuota(); // Refresh quota
      
      // Optionally read aloud first question (default off to minimize voice)
      if (readAloud) setTimeout(() => playQuestion(questionList[0]), 500);
      
    } catch (error) {
      console.error('Error starting interview:', error);
      alert('Failed to start interview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const playQuestion = async (question) => {
    try {
      if (!readAloud) return; // Text-only by default
      setAudioPlaying(true);
      const audioBlob = await textToSpeech(question, selectedCoach || 'Nova');
      const audioUrl = URL.createObjectURL(audioBlob);
      audioRef.current.src = audioUrl;
      audioRef.current.onended = () => setAudioPlaying(false);
      await audioRef.current.play();
    } catch (error) {
      console.error('Error playing question:', error);
      setAudioPlaying(false);
    }
  };

  const submitResponse = async () => {
    // Validate user response
    const validation = validateUserResponse(userResponse, 10);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    // Clear any previous errors
    setValidationError(null);

    const currentQIndex = currentQuestion;
    const currentAnswer = userResponse;

    setResponses([...responses, {
      question: questions[currentQIndex],
      response: currentAnswer
    }]);

    setUserResponse('');

    // Always analyze to enable retry coaching panel (non-blocking)
    analyzeAnswer(currentQIndex, questions[currentQIndex], currentAnswer).catch((err) => {
      console.warn('Analysis failed:', err?.message || err);
    });
    setAwaitingRetry(true);

    // Do not advance automatically; wait for user to retry or continue
  };

  // Generate per-question live analysis (STAR critique, brief tips)
  const analyzeAnswer = async (index, question, answer) => {
    try {
      // Call STAR analyzer endpoint
      const response = await fetch('https://us-central1-fidora-72b3d.cloudfunctions.net/analyzeSTAR', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          response: answer,
          question: question,
          userId: user?.uid
        })
      });
      
      if (!response.ok) {
        throw new Error('STAR analysis failed');
      }
      
      const data = await response.json();
      const analysis = data.analysis;
      
      // Store analysis for this question
      setLiveAnalysis(prev => ({
        ...prev,
        [index]: analysis
      }));
      
      // Show micro-coaching tip immediately
      const tipMessage = `💡 ${analysis.microTip}\n\nSTAR Score: S:${(analysis.starScore.S * 100).toFixed(0)}% T:${(analysis.starScore.T * 100).toFixed(0)}% A:${(analysis.starScore.A * 100).toFixed(0)}% R:${(analysis.starScore.R * 100).toFixed(0)}%`;
      
      // Display as a notification (you can enhance this with a toast/modal)
      alert(tipMessage);
      
    } catch (error) {
      console.error('Live analysis error:', error);
    }
  };

  const generateFeedback = async () => {
    setLoading(true);
    setInterviewStep('feedback');

    try {
      const interviewTranscript = responses.map((r, i) => 
        `Q${i + 1}: ${r.question}\nA${i + 1}: ${r.response}`
      ).join('\n\n');

      const feedbackLines = [];
      feedbackLines.push(`You are ${selectedCoach || 'Nova'}, providing detailed interview feedback and coaching.`);
      feedbackLines.push(`Role Context: ${difficulty} ${jobRole}`);
      if (alignWithCoachData && hasCoachData && coachStory) {
        feedbackLines.push(`User Resume/Coach Context (use to tailor advice, do not repeat verbatim): ${JSON.stringify(coachStory).slice(0, 4000)}`);
      }
      feedbackLines.push('Interview Transcript:');
      feedbackLines.push(interviewTranscript);
      feedbackLines.push('Please provide a comprehensive but concise report with:');
      feedbackLines.push('1. Overall Performance Score (1-10) with rationale');
      feedbackLines.push('2. STAR Coverage Assessment: where S/T/A/R were clear or missing');
      feedbackLines.push('3. Key Strengths (3-4 bullets)');
      feedbackLines.push('4. Areas for Improvement (3-4 bullets)');
      feedbackLines.push('5. Targeted rewrites: 1-2 example improved sentences for weak parts');
      feedbackLines.push('6. Alignment to resume/coach data: specific suggestions (if provided)');
      feedbackLines.push('7. One actionable next step for practice');
      feedbackLines.push('Be encouraging but honest. Reference specific moments from answers.');

      const feedbackPrompt = feedbackLines.join('\n\n');

      const response = await sendMessage(
        'Please provide detailed feedback on my mock interview performance.',
        [{ role: 'system', content: feedbackPrompt }],
        user.displayName || user.email
      );

      setFeedback(response.reply);
      // Also save transcript + feedback for review/adaptive use later
      try {
        const msgs = [];
        responses.forEach((r, i) => {
          msgs.push({ role: 'system', content: `Q${i + 1}: ${r.question}` });
          msgs.push({ role: 'user', content: r.response });
          const la = liveAnalysis[i];
          if (la) msgs.push({ role: 'assistant', content: `STAR Tip: ${la.microTip}` });
        });
        msgs.push({ role: 'assistant', content: `Full Feedback:\n${response.reply}` });
        documentStorage.saveTranscript(user.uid, msgs, `Mock Interview ${new Date().toLocaleDateString()}`);
        
        // Save to Firestore mockInterviews collection
        try {
          const db = getFirestore();
          const interviewData = {
            userId: user.uid,
            userEmail: user.email || '',
            interviewType: interviewType,
            difficulty: difficulty,
            jobRole: jobRole,
            questions: questions,
            responses: responses.map((r, i) => ({
              question: r.question,
              response: r.response,
              questionNumber: i + 1,
              liveAnalysis: liveAnalysis[i] || null
            })),
            feedback: response.reply,
            coach: selectedCoach || 'Nova',
            analysisMode: analysisMode,
            alignWithCoachData: alignWithCoachData,
            createdAt: serverTimestamp(),
            completedAt: serverTimestamp(),
            duration: Date.now() - (interviewStartTime || Date.now())
          };
          await addDoc(collection(db, 'mockInterviews'), interviewData);
          console.log('✅ Mock interview saved to Firestore');
        } catch (firestoreError) {
          console.error('Error saving interview to Firestore:', firestoreError);
          // Continue even if Firestore save fails
        }
      } catch (localError) {
        console.error('Error saving transcript locally:', localError);
      }
      // Compute and store adaptive focus for next session
      try {
        const focus = buildAdaptiveFocusFromSession();
        localStorage.setItem(`fidora_adaptive_focus_${user?.uid || 'anon'}`, JSON.stringify({ focus, ts: Date.now() }));
      } catch {}
      
    } catch (error) {
      console.error('Error generating feedback:', error);
      alert('Failed to generate feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetInterview = () => {
    setInterviewStep('setup');
    setQuestions([]);
    setCurrentQuestion(0);
    setResponses([]);
    setUserResponse('');
    setFeedback(null);
    setValidationError(null);
    setJobRoleSuggestion(null);
    setAwaitingRetry(false);
    setInterviewStartTime(null);
    loadQuota();
  };

  const saveSessionLocally = async (titleSuffix = '') => {
    try {
      if (!user || !user.uid) return;
      const msgs = [];
      responses.forEach((r, i) => {
        msgs.push({ role: 'system', content: `Q${i + 1}: ${r.question}` });
        msgs.push({ role: 'user', content: r.response });
        const la = liveAnalysis[i];
        if (la) msgs.push({ role: 'assistant', content: `STAR Tip: ${la.microTip}` });
      });
      const title = `Mock Interview ${new Date().toLocaleDateString()}${titleSuffix}`;
      documentStorage.saveTranscript(user.uid, msgs, title);
    } catch (e) {
      console.warn('Could not save session:', e?.message || e);
    }
  };

  // Build concise adaptive focus (1-2 bullets) from current session STAR scores
  function buildAdaptiveFocusFromSession() {
    try {
      const indices = Object.keys(liveAnalysis).map(i => parseInt(i, 10)).filter(n => !Number.isNaN(n));
      if (indices.length === 0) return [];
      let sum = { S: 0, T: 0, A: 0, R: 0 };
      indices.forEach(i => {
        const sc = liveAnalysis[i]?.starScore || { S: 0, T: 0, A: 0, R: 0 };
        sum.S += sc.S || 0; sum.T += sc.T || 0; sum.A += sc.A || 0; sum.R += sc.R || 0;
      });
      const avg = { S: sum.S / indices.length, T: sum.T / indices.length, A: sum.A / indices.length, R: sum.R / indices.length };
      const entries = Object.entries(avg).sort((a, b) => a[1] - b[1]);
      const focus = [];
      for (const [k] of entries) {
        if (focus.length >= 2) break;
        if (k === 'S') focus.push('Clarify Situation succinctly');
        if (k === 'T') focus.push('State Task/goal explicitly');
        if (k === 'A') focus.push('Detail Actions with specifics');
        if (k === 'R') focus.push('Quantify Results with metrics');
      }
      return focus.length > 0 ? focus : ['Tighten answers and emphasize outcomes'];
    } catch {
      return [];
    }
  }

  // Render Setup Step
  if (interviewStep === 'setup') {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${theme.bg} 0%, ${darkMode ? '#312e81' : '#fde68a'} 100%)`,
        color: theme.text,
        padding: '20px',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '10px', letterSpacing: '-0.02em' }}>
              Mock Interview
            </h1>
            <p style={{ fontSize: '18px', color: theme.subtext, fontWeight: '400' }}>
              Enterprise-grade AI interview preparation
            </p>
            
            {quota ? (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: quota.remaining > 0 ? theme.card : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${quota.remaining > 0 ? theme.border : '#ef4444'}`,
                borderRadius: '12px',
                display: 'inline-block'
              }}>
                {quota.allowed ? (
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    <span style={{ fontWeight: '600' }}>{quota.remaining}</span> mock interview{quota.remaining !== 1 ? 's' : ''} remaining this month
                  </p>
                ) : (
                  <p style={{ margin: 0, fontSize: '14px', color: theme.error }}>
                    {quota.reason || 'Unable to load quota information'}
                  </p>
                )}
              </div>
            ) : (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '12px',
                display: 'inline-block'
              }}>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Loading quota information...
                </p>
              </div>
            )}
          </div>

          {/* Always show setup form, even if quota is loading */}
          {(!quota || quota.allowed) && (
            <>
              {/* Coach Selection */}
              <div style={{
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '16px',
                padding: '30px',
                marginBottom: '30px'
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '15px' }}>
                  Select Your Interviewer
                </h2>
                <select
                  value={selectedCoach || 'Nova'}
                  onChange={(e) => {
                    const coach = e.target.value;
                    setSelectedCoach(coach);
                    if (onSelectCoach) {
                      onSelectCoach(coach);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.border}`,
                    background: darkMode ? 'rgba(30, 27, 75, 0.5)' : 'white',
                    color: theme.text
                  }}
                >
                  <option value="Nova">Nova - Career Coach (Supportive & Comprehensive)</option>
                  <option value="Atlas">Atlas - Strategic Advisor (Visionary & Analytical)</option>
                  <option value="Sage">Sage - Wisdom Coach (Mature & Grounded)</option>
                  <option value="Phoenix">Phoenix - Growth Coach (Energizing & Transformative)</option>
                  <option value="Aurora">Aurora - Career Navigator (Insightful & Guiding)</option>
                  <option value="Catalyst">Catalyst - Change Agent (Dynamic & Innovative)</option>
                </select>
                <p style={{ fontSize: '13px', color: theme.subtext, marginTop: '10px', lineHeight: '1.5' }}>
                  Choose your AI coach to conduct the interview. Each coach has a unique style and expertise.
                </p>
              </div>

              {/* Interview Type */}
              <div style={{
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '16px',
                padding: '30px',
                marginBottom: '30px'
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                  Interview Type
                </h2>
                <p style={{ fontSize: '13px', color: theme.subtext, marginBottom: '15px', lineHeight: '1.5' }}>
                  💡 Questions are dynamically generated fresh each time based on current industry trends and your specific role.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {Object.entries(interviewTypes).map(([key, type]) => (
                    <button
                      key={key}
                      onClick={() => setInterviewType(key)}
                      style={{
                        padding: '20px',
                        borderRadius: '12px',
                        border: `2px solid ${interviewType === key ? theme.accent : theme.border}`,
                        background: interviewType === key ? `${theme.accent}15` : 'transparent',
                        color: theme.text,
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '16px' }}>{type.name}</div>
                      <div style={{ fontSize: '13px', color: theme.subtext, lineHeight: '1.5' }}>{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty & Job Role */}
              <div style={{
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '16px',
                padding: '30px',
                marginBottom: '30px'
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                  Interview Details
                </h2>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Experience Level
                  </label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      background: darkMode ? 'rgba(30, 27, 75, 0.5)' : 'white',
                      color: theme.text
                    }}
                  >
                    {Object.entries(difficulties).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Job Role / Position
                  </label>
                  <input
                    type="text"
                    value={jobRole}
                    onChange={(e) => {
                      setJobRole(e.target.value);
                      // Clear previous errors when user types
                      setValidationError(null);
                      
                      // Check for suggestions as user types
                      if (e.target.value.trim().length >= 3) {
                        const validation = validateJobRole(e.target.value);
                        if (validation.suggestion) {
                          setJobRoleSuggestion(validation.suggestion);
                        } else {
                          setJobRoleSuggestion(null);
                        }
                      } else {
                        setJobRoleSuggestion(null);
                      }
                    }}
                    placeholder="e.g., Software Engineer, Marketing Manager, Product Designer"
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      borderRadius: '8px',
                      border: `1px solid ${validationError ? theme.error : theme.border}`,
                      background: darkMode ? 'rgba(30, 27, 75, 0.5)' : 'white',
                      color: theme.text
                    }}
                  />
                  
                  {/* Validation Error */}
                  {validationError && (
                    <div style={{
                      marginTop: '8px',
                      padding: '10px 12px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: theme.error,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>⚠️</span>
                      <span>{validationError}</span>
                    </div>
                  )}
                  
                  {/* Job Role Suggestion */}
                  {jobRoleSuggestion && !validationError && (
                    <div style={{
                      marginTop: '8px',
                      padding: '10px 12px',
                      background: `${theme.accent}10`,
                      border: `1px solid ${theme.accent}30`,
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: theme.subtext
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        💡 Did you mean: <strong style={{ color: theme.text }}>{jobRoleSuggestion}</strong>?
                      </div>
                      <button
                        onClick={() => {
                          setJobRole(jobRoleSuggestion);
                          setJobRoleSuggestion(null);
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          borderRadius: '6px',
                          border: `1px solid ${theme.accent}`,
                          background: 'transparent',
                          color: theme.accent,
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Use this
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Optional: Customize & Intake for this session */}
              <div style={{
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '16px',
                padding: '30px',
                marginBottom: '30px'
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '10px' }}>
                  Customize This Session (Optional)
                </h2>
                <div style={{ display: 'grid', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Additional questions you want included (one per line)</label>
                    <textarea rows={4} value={intake.additionalQuestions}
                      onChange={(e) => setIntake({ ...intake, additionalQuestions: e.target.value })}
                      placeholder="e.g., Tell me about a time you managed conflict\nWhat would you do in your first 90 days?"
                      style={{ width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${theme.border}`, background: darkMode ? 'rgba(30, 27, 75, 0.5)' : 'white', color: theme.text }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Notes for your coach (focus areas, concerns)</label>
                    <textarea rows={3} value={intake.notesForCoach}
                      onChange={(e) => setIntake({ ...intake, notesForCoach: e.target.value })}
                      placeholder="e.g., I want to improve speaking concisely and STAR results"
                      style={{ width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${theme.border}`, background: darkMode ? 'rgba(30, 27, 75, 0.5)' : 'white', color: theme.text }} />
                  </div>
                </div>
              </div>

            {/* Coaching Mode */}
            <div style={{
              background: theme.card,
              border: `1px solid ${theme.border}`,
              borderRadius: '16px',
              padding: '30px',
              marginBottom: '30px'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
                Coaching Mode
              </h2>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="coaching_mode"
                    checked={analysisMode === 'summary'}
                    onChange={() => setAnalysisMode('summary')}
                  />
                  <span>End summary (feedback after all questions)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="coaching_mode"
                    checked={analysisMode === 'live'}
                    onChange={() => setAnalysisMode('live')}
                  />
                  <span>Live coaching (brief tips after each answer)</span>
                </label>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <input type="checkbox" checked={adaptiveFocusEnabled} onChange={(e) => setAdaptiveFocusEnabled(e.target.checked)} />
                <span>Use adaptive focus from previous sessions (keeps it helpful, not heavy)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: hasCoachData ? 1 : 0.7 }} title={hasCoachData ? '' : 'Complete your resume/story with your coach to enable alignment.'}>
                <input
                  type="checkbox"
                  checked={alignWithCoachData && hasCoachData}
                  onChange={(e) => setAlignWithCoachData(e.target.checked)}
                  disabled={!hasCoachData}
                />
                <span>Align feedback with my resume and coach data</span>
              </label>
              {!hasCoachData && (
                <div style={{ marginTop: 8, fontSize: 13, color: theme.subtext }}>
                  Tip: Meet your personal coach and complete your resume/story to unlock personalized alignment.
                </div>
              )}
            </div>

              {/* Start Button */}
              <button
                onClick={startInterview}
                disabled={loading || !jobRole.trim()}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: '600',
                  borderRadius: '12px',
                  border: 'none',
                  background: theme.accent,
                  color: 'white',
                  cursor: loading || !jobRole.trim() ? 'not-allowed' : 'pointer',
                  opacity: loading || !jobRole.trim() ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {loading ? 'Preparing Interview...' : 'Start Mock Interview'}
              </button>
            </>
          )}

          {quota && !quota.allowed && quota.upgradeRequired && (
            <button
              onClick={() => navigate('/subscription')}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                fontWeight: '600',
                borderRadius: '12px',
                border: 'none',
                background: theme.accent,
                color: 'white',
                cursor: 'pointer',
                marginTop: '20px'
              }}
            >
              Upgrade to Access Mock Interviews
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render Interview Step
  if (interviewStep === 'interview') {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${theme.bg} 0%, ${darkMode ? '#312e81' : '#fde68a'} 100%)`,
        color: theme.text,
        padding: '20px',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Top actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.subtext }}>
              <input type="checkbox" checked={readAloud} onChange={(e) => setReadAloud(e.target.checked)} />
              Read questions aloud (uses voice minutes)
            </label>
            <button
              onClick={async () => {
                const save = window.confirm('Cancel session? You can save this session to review later. Click OK to save & cancel, or Cancel to discard.');
                if (save) await saveSessionLocally(' (cancelled)');
                resetInterview();
              }}
              style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${theme.border}`, background: 'transparent', color: theme.text, cursor: 'pointer', fontSize: 14 }}
            >
              Cancel Session
            </button>
          </div>
          {/* Progress */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                Question {currentQuestion + 1} of {questions.length}
              </span>
              <span style={{ fontSize: '14px', color: theme.subtext }}>
                {Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete
              </span>
            </div>
            <div style={{
              height: '8px',
              background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${((currentQuestion + 1) / questions.length) * 100}%`,
                background: theme.accent,
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* Question Card */}
          <div style={{
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '16px',
            padding: '40px',
            marginBottom: '30px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: theme.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                marginRight: '15px'
              }}>
                Q{currentQuestion + 1}
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: theme.subtext }}>
                  {selectedCoach || 'Nova'} asks:
                </div>
                <div style={{ fontSize: '12px', color: theme.subtext }}>
                  {interviewTypes[interviewType].name}
                </div>
              </div>
              
              <button
                onClick={() => playQuestion(questions[currentQuestion])}
                disabled={audioPlaying}
                style={{
                  marginLeft: 'auto',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.border}`,
                  background: audioPlaying ? theme.accent : 'transparent',
                  color: theme.text,
                  cursor: audioPlaying ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {audioPlaying ? '🔊 Playing...' : '🔊 Read Aloud'}
              </button>
            </div>

            <h2 style={{ fontSize: '24px', lineHeight: '1.5', fontWeight: '500', margin: 0 }}>
              {questions[currentQuestion]}
            </h2>
          </div>

          {/* Live Coaching for previous answer */}
          {analysisMode === 'live' && typeof liveFeedbackByIndex[currentQuestion - 1] === 'string' && (
            <div style={{
              background: `${theme.accent}10`,
              border: `1px solid ${theme.accent}30`,
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6',
              fontSize: '14px',
              color: theme.text
            }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Live coaching on your previous answer</div>
              {liveFeedbackByIndex[currentQuestion - 1]}
            </div>
          )}

          {/* Response Area */}
          <div style={{
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '16px',
            padding: '30px',
            marginBottom: '20px'
          }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
              Your Response
            </label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button
                onClick={() => {
                  try {
                    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
                    if (!SpeechRecognition) {
                      alert('Speech recognition is not supported in this browser. Use Chrome/Edge/Safari or type your answer.');
                      return;
                    }
                    if (isRecording) return;
                    navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => {});
                    const recognition = new SpeechRecognition();
                    recognition.lang = 'en-US';
                    recognition.continuous = true;
                    recognition.interimResults = true;
                    recognition.onstart = () => setIsRecording(true);
                    let finalText = userResponse || '';
                    let silenceTimer = null;
                    recognition.onresult = (event) => {
                      let interim = '';
                      for (let i = event.resultIndex; i < event.results.length; i++) {
                        const transcript = event.results[i][0].transcript;
                        if (event.results[i].isFinal) {
                          finalText += (finalText ? ' ' : '') + transcript.trim();
                        } else {
                          interim += transcript;
                        }
                      }
                      setUserResponse((finalText + ' ' + interim).trim());
                      if (silenceTimer) clearTimeout(silenceTimer);
                      silenceTimer = setTimeout(() => { try { recognition.stop(); } catch {} }, 2000);
                    };
                    recognition.onerror = () => setIsRecording(false);
                    recognition.onend = () => setIsRecording(false);
                    recognitionRef.current = recognition;
                    recognition.start();
                  } catch (err) {
                    console.error('Mic start failed:', err);
                    alert('Could not start microphone. Please check permissions.');
                  }
                }}
                disabled={isRecording}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: `1px solid ${theme.border}`,
                  background: isRecording ? 'rgba(239,68,68,0.15)' : 'transparent',
                  color: theme.text,
                  cursor: isRecording ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                {isRecording ? '🎙️ Recording...' : '🎤 Use microphone'}
              </button>
              <button
                onClick={() => { try { recognitionRef.current && recognitionRef.current.stop(); } catch {} setIsRecording(false); }}
                disabled={!isRecording}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: `1px solid ${theme.border}`,
                  background: !isRecording ? 'transparent' : 'rgba(239,68,68,0.15)',
                  color: theme.text,
                  cursor: !isRecording ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                ⏹ Stop
              </button>
            </div>
            <textarea
              value={userResponse}
              onChange={(e) => {
                setUserResponse(e.target.value);
                // Clear validation error when user types
                if (validationError) {
                  setValidationError(null);
                }
              }}
              placeholder="Type your answer here... Try to be specific and provide examples."
              rows={8}
              style={{
                width: '100%',
                padding: '15px',
                fontSize: '16px',
                borderRadius: '8px',
                border: `1px solid ${validationError ? theme.error : theme.border}`,
                background: darkMode ? 'rgba(30, 27, 75, 0.5)' : 'white',
                color: theme.text,
                resize: 'vertical',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            />
            
            {/* Validation Error */}
            {validationError && (
              <div style={{
                marginTop: '10px',
                padding: '10px 12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                fontSize: '13px',
                color: theme.error,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>⚠️</span>
                <span>{validationError}</span>
              </div>
            )}
            
            {interviewType === 'behavioral' && (
              <div style={{
                marginTop: '15px',
                padding: '12px',
                background: `${theme.accent}10`,
                borderRadius: '8px',
                fontSize: '13px',
                color: theme.subtext
              }}>
                💡 <strong>STAR Method Tip:</strong> Structure your answer with Situation, Task, Action, and Result
              </div>
            )}
          </div>

          {/* Coaching panel and actions */}
          {awaitingRetry && liveAnalysis[currentQuestion] && (
            <div style={{
              background: `${theme.accent}10`,
              border: `1px solid ${theme.accent}30`,
              borderRadius: '12px',
              padding: '16px',
              marginTop: 12,
              marginBottom: 12,
              lineHeight: 1.6,
              fontSize: 14
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Coach Feedback (text-only):</div>
              <div>STAR Scores — S: {(liveAnalysis[currentQuestion].starScore.S * 100).toFixed(0)}% · T: {(liveAnalysis[currentQuestion].starScore.T * 100).toFixed(0)}% · A: {(liveAnalysis[currentQuestion].starScore.A * 100).toFixed(0)}% · R: {(liveAnalysis[currentQuestion].starScore.R * 100).toFixed(0)}%</div>
              <div style={{ marginTop: 6 }}>Tip: {liveAnalysis[currentQuestion].microTip}</div>
              <div style={{ marginTop: 10, fontSize: 13, color: theme.subtext }}>You can edit your response above and retry as many times as you want.</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={submitResponse}
              disabled={!userResponse.trim()}
              style={{
                flex: 1,
                padding: '16px',
                fontSize: '18px',
                fontWeight: '600',
                borderRadius: '12px',
                border: 'none',
                background: theme.accent,
                color: 'white',
                cursor: !userResponse.trim() ? 'not-allowed' : 'pointer',
                opacity: !userResponse.trim() ? 0.5 : 1
              }}
            >
              Get Coach Feedback
            </button>
            <button
              onClick={() => {
                // Continue to next question
                const nextQ = currentQuestion + 1;
                if (nextQ < questions.length) {
                  setCurrentQuestion(nextQ);
                  setUserResponse('');
                  setAwaitingRetry(false);
                  if (readAloud) playQuestion(questions[nextQ]);
                } else {
                  generateFeedback();
                }
              }}
              style={{
                padding: '16px',
                fontSize: '18px',
                fontWeight: '600',
                borderRadius: '12px',
                border: `1px solid ${theme.border}`,
                background: 'transparent',
                color: theme.text,
                cursor: 'pointer'
              }}
            >
              {currentQuestion < questions.length - 1 ? 'Next Question' : 'Finish & Full Feedback'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render Feedback Step
  if (interviewStep === 'feedback') {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${theme.bg} 0%, ${darkMode ? '#312e81' : '#fde68a'} 100%)`,
        color: theme.text,
        padding: '20px',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ fontSize: '60px', marginBottom: '10px' }}>🎉</div>
            <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '10px' }}>
              Interview Complete!
            </h1>
            <p style={{ fontSize: '18px', color: theme.subtext }}>
              Great job completing the interview. Here's your detailed feedback.
            </p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                border: `4px solid ${theme.border}`,
                borderTop: `4px solid ${theme.accent}`,
                borderRadius: '50%',
                margin: '0 auto 20px',
                animation: 'spin 1s linear infinite'
              }} />
              <p style={{ fontSize: '16px', color: theme.subtext }}>
                Analyzing your performance...
              </p>
            </div>
          ) : feedback && (
            <>
              {/* Feedback Card */}
              <div style={{
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '16px',
                padding: '40px',
                marginBottom: '30px',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.8'
              }}>
                <div style={{ fontSize: '16px' }}>{feedback}</div>
              </div>

              {/* Post-session intake feedback */}
              <div style={{
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '20px'
              }}>
                <h3 style={{ marginTop: 0 }}>How did it feel? (Optional)</h3>
                <div style={{ display: 'grid', gap: 12 }}>
                  <textarea rows={3} value={intake.feelings}
                    onChange={(e) => setIntake({ ...intake, feelings: e.target.value })}
                    placeholder="Share how that felt, what was hard, what improved"
                    style={{ width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${theme.border}`, background: darkMode ? 'rgba(30, 27, 75, 0.5)' : 'white', color: theme.text }} />
                  <textarea rows={3} value={intake.nextSteps}
                    onChange={(e) => setIntake({ ...intake, nextSteps: e.target.value })}
                    placeholder="Next steps you want to take before the next session"
                    style={{ width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${theme.border}`, background: darkMode ? 'rgba(30, 27, 75, 0.5)' : 'white', color: theme.text }} />
                </div>
                <div style={{ fontSize: 13, color: theme.subtext, marginTop: 8 }}>Your notes will be saved with the session and used to improve the next one.</div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '15px' }}>
                <button
                  onClick={async () => { await saveSessionLocally(); resetInterview(); }}
                  style={{
                    flex: 1,
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: `1px solid ${theme.border}`,
                    background: 'transparent',
                    color: theme.text,
                    cursor: 'pointer'
                  }}
                >
                  Start New Interview
                </button>
                <button
                  onClick={async () => { await saveSessionLocally(); navigate('/'); }}
                  style={{
                    flex: 1,
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: 'none',
                    background: theme.accent,
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Return to Chat
                </button>
              </div>
            </>
          )}
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return null;
}

